import React, { useMemo, useCallback } from "react";
import { 
  Box, 
  Button, 
  VStack, 
  Text, 
  useColorModeValue, 
  Flex, 
  Icon, 
  Spinner, 
  Collapse, 
  Heading,
  HStack,
  Badge,
} from "@chakra-ui/react";
import { Reorder } from "framer-motion";
import CustomInput from '../../commonComponents/input';
import LoadingSpinner from '../../commonComponents/LoadingSpinner';
import ErrorState from '../../commonComponents/ErrorState';
import MenuItem from "./MenuItem";
import { 
  MdDragHandle, 
  MdRestaurant,
  MdCategory
} from "react-icons/md";
import { ChevronDownIcon, ChevronUpIcon, AddIcon } from "@chakra-ui/icons";
import { useOptimizedMenuData } from '../../hooks/useOptimizedMenuData';
import { SEARCH, LOADING_STATES, ERROR_STATES } from './Constant';


const MenuOverview = ({
  refreshTrigger,
  handleAddItem,
  categoriesToRefetch,
  onCategoriesRefetched,
  onCustomizationClick,
  setCustomizations,
  setCategories,
  setEditingItem,
  setImageFile,
  setIsAddItemOpen,
}) => {

  const {
    categories,
    categoryItemsMap,
    openedCategories,
    query,
    localQuery,
    hasMore,
    loadingItems,
    expandAll,
    isLoading,
    error,
    inViewRef,
    handleSearchChange,
    clearSearch,
    handleToggleCategory,
    handleReorder,
    handleReorderItems,
    toggleAllCategories,
    handleDelete,
    data,
    page,
  } = useOptimizedMenuData({
    refreshTrigger,
    categoriesToRefetch,
    onCategoriesRefetched,
  });

  const bgColor = useColorModeValue("white", "gray.800");
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textColor = useColorModeValue("gray.800", "white");
  const subtleTextColor = useColorModeValue("gray.600", "gray.300");
  const accentColor = useColorModeValue("blue.500", "blue.400");
  const hoverBg = useColorModeValue("gray.50", "gray.700");

  const filteredCategories = useMemo(() => {
    if (!query) return categories;
    const lowerQuery = query.toLowerCase();
    return categories.filter((cat) => {
      if (cat.category.toLowerCase().includes(lowerQuery)) {
        return true;
      }
      const items = categoryItemsMap[cat.id] || [];
      return items.some((item) =>
        item.item?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery)
      );
    });
  }, [categories, query, categoryItemsMap]);

  const handleToggle = useCallback((categoryId, itemsLength) => {
    handleToggleCategory(categoryId);
  }, [handleToggleCategory]);

  const handleConfirmDelete = (id) => {
    handleDelete(id);
  };

  return (
    <Box>
      {/* Enhanced Header Section */}
      <Box
        p={6}
        mb={6}
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        shadow="sm"
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={4}
        >
          <VStack align={{ base: "center", md: "flex-start" }} spacing={3}>
            <HStack spacing={3}>
              <Box
                p={3}
                borderRadius="xl"
                bg={`${accentColor}20`}
                color={accentColor}
              >
                <Icon as={MdRestaurant} boxSize={6} />
              </Box>
              <VStack align={{ base: "center", md: "flex-start" }} spacing={1}>
                <Heading size="lg" color={textColor} fontWeight="700">
                  Menu Overview
                </Heading>
                <Text fontSize="md" color={subtleTextColor} textAlign={{ base: "center", md: "left" }}>
                  Manage your menu items and categories
                </Text>
              </VStack>
            </HStack>
          </VStack>
          
          <VStack spacing={3} align={{ base: "center", md: "flex-end" }}>
            <HStack spacing={3} justify={{ base: "center", md: "flex-end" }}>
              <Button
                size="md"
                leftIcon={<AddIcon />}
                onClick={handleAddItem}
                colorScheme="blue"
                borderRadius="full"
                _hover={{ transform: "translateY(-1px)", boxShadow: "lg" }}
                transition="all 0.2s ease-in-out"
              >
                Add Item
              </Button>
              <Button
                size="md"
                variant="outline"
                onClick={toggleAllCategories}
                borderRadius="full"
                _hover={{ bg: "gray.50" }}
              >
                {expandAll ? "Collapse All" : "Expand All"}
              </Button>
            </HStack>
          </VStack>
        </Flex>
      </Box>
      
      {/* Enhanced Search Section */}
      <Box
        p={4}
        mb={6}
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        borderRadius="xl"
        shadow="sm"
      >
        <VStack spacing={4}>
          <CustomInput 
            value={localQuery}
            onChange={(value) => {
              // Create a synthetic event-like object for handleSearchChange
              const syntheticEvent = {
                target: { value }
              };
              handleSearchChange(syntheticEvent);
            }}
            placeholder={SEARCH.PLACEHOLDER}
            allowClear
            onClear={clearSearch}
          />
          
          {/* Quick Stats */}
          <HStack spacing={6} justify="center" w="full">
            <HStack spacing={2}>
              <Icon as={MdCategory} color={accentColor} />
              <Text fontSize="sm" color={subtleTextColor}>
                {filteredCategories.length} Categories
              </Text>
            </HStack>
            <HStack spacing={2}>
              <Icon as={MdRestaurant} color={accentColor} />
              <Text fontSize="sm" color={subtleTextColor}>
                {Object.values(categoryItemsMap).flat().length} Total Items
              </Text>
            </HStack>
          </HStack>


        </VStack>
      </Box>



      {/* Main Content */}
      {isLoading ? (
        <LoadingSpinner text={LOADING_STATES.LOADING_MENU} />
      ) : error ? (
        <ErrorState 
          message={ERROR_STATES.FAILED_LOAD_MENU}
          onRetry={() => window.location.reload()}
        />
      ) : !categories || categories.length === 0 ? (
        <Box
          p={8}
          bg={cardBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          textAlign="center"
        >
          <VStack spacing={4}>
            <Box
              p={4}
              borderRadius="full"
              bg={`${accentColor}20`}
              color={accentColor}
            >
              <Icon as={MdRestaurant} boxSize={8} />
            </Box>
            <VStack spacing={2}>
              <Text fontSize="lg" fontWeight="600" color={textColor}>
                {ERROR_STATES.NO_CATEGORIES}
              </Text>
              <Text fontSize="sm" color={subtleTextColor}>
                Start building your menu by creating your first category
              </Text>
            </VStack>
            <Button 
              onClick={handleAddItem}
              colorScheme="blue"
              leftIcon={<AddIcon />}
              borderRadius="full"
            >
              {ERROR_STATES.CREATE_FIRST_CATEGORY}
            </Button>
          </VStack>
        </Box>
      ) : (
        <Box p={4} bg={bgColor} borderRadius="xl" border="1px solid" borderColor={borderColor} shadow="sm">
          <Reorder.Group as="div" axis="y" values={filteredCategories.map((cat) => cat.id)} onReorder={handleReorder}>
            {filteredCategories.map((category) => {
              const items = categoryItemsMap[category.id] || [];
              const isOpen = openedCategories.includes(category.id);
              const categoryLoadingItems = loadingItems[category.id];

              return (
                <Reorder.Item as="div" key={category.id} value={category.id}>
                  <VStack spacing={2} align="stretch" width="100%" mb={4}>
                    <Flex
                      justify="space-between"
                      align="center"
                      p={{ base: 4, md: 6 }}
                      bg={cardBg}
                      boxShadow="sm"
                      cursor="pointer"
                      onClick={() => handleToggle(category.id, items.length)}
                      border="1px solid"
                      borderColor={borderColor}
                      width="100%"
                      borderRadius="xl"
                      position="relative"
                      minH={{ base: "60px", md: "auto" }}
                      _active={{ transform: "scale(0.98)" }}
                      transition="all 0.2s"
                      _hover={{ bg: hoverBg, shadow: "md" }}
                    >
                      <Box
                        cursor="grab"
                        mr={{ base: 3, md: 4 }}
                        p={{ base: 2, md: 2 }}
                        borderRadius="lg"
                        _active={{ cursor: "grabbing" }}
                        _hover={{ bg: "gray.100" }}
                      >
                        <Icon
                          as={MdDragHandle}
                          w={{ base: 6, md: 5 }}
                          h={{ base: 6, md: 5 }}
                          color="gray.500"
                        />
                      </Box>

                      <Flex align="center" flex="1">
                        <VStack align="flex-start" spacing={2} ml={{ base: 2, md: 4 }}>
                          <HStack spacing={3}>
                            <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="700" color={textColor}>
                              {category.category}
                            </Text>
                            <Badge
                              colorScheme="blue"
                              variant="subtle"
                              borderRadius="full"
                              px={3}
                              py={1}
                              fontSize="sm"
                            >
                              {items.length} items
                            </Badge>
                          </HStack>
                          <Text fontSize="sm" color={subtleTextColor}>
                            Click to {isOpen ? "collapse" : "expand"} items
                          </Text>
                        </VStack>
                      </Flex>
                      <Box p={2}>
                        {isOpen ? (
                          <Icon as={ChevronUpIcon} boxSize={6} color={accentColor} />
                        ) : (
                          <Icon as={ChevronDownIcon} boxSize={6} color={subtleTextColor} />
                        )}
                      </Box>
                    </Flex>

                    <Collapse
                      in={isOpen}
                      animateOpacity
                      style={{
                        transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
                        transitionDuration: "100ms",
                      }}
                    >
                      <Box px={{ base: "0", md: "2" }}>
                        {categoryLoadingItems ? (
                          <Box py={4} textAlign="center">
                            <Spinner size="md" color={accentColor} />
                          </Box>
                        ) : (
                          <Reorder.Group
                            axis="y"
                            values={items.map(item => item.id)}
                            onReorder={(newOrderIds) => {
                              const newOrder = newOrderIds.map(id => items.find(item => item.id === id)).filter(Boolean);
                              handleReorderItems(category.id, items, newOrder);
                            }}
                            style={{ willChange: "transform" }}
                          >
                            {items.map((item) => (
                              <Reorder.Item
                                key={item.id}
                                value={item.id}
                                style={{ willChange: "transform" }}
                              >
                                <MenuItem
                                  item={item}
                                  categoryId={category.id}
                                  updateItemOrder={() => {}}
                                  onDelete={handleConfirmDelete}
                                  onCustomizationClick={onCustomizationClick}
                                  setCustomizations={setCustomizations}
                                  setCategories={setCategories}
                                  setEditingItem={setEditingItem}
                                  setImageFile={setImageFile}
                                  setIsAddItemOpen={setIsAddItemOpen}
                                />
                              </Reorder.Item>
                            ))}
                          </Reorder.Group>
                        )}
                      </Box>
                    </Collapse>
                  </VStack>
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
          
          {hasMore && data?.meta?.totalPages > 1 && page < data.meta.totalPages ? (
            <LoadingSpinner 
              size="md" 
              text="" 
              py={4} 
              ref={inViewRef}
              showText={false}
            />
          ) : null}
        </Box>
      )}

    </Box>
  );
};

export default React.memo(MenuOverview);
